import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createOrganisationInvite,
  createOrganisationRole,
  createPlatformOrganisation,
  deleteOrganisationInvite,
  deleteOrganisationRole,
  getInviteEligiblePersonnel,
  getOrganisationInvites,
  getOrganisationRoles,
  getOrganisationSettings,
  getOrganisationUsers,
  getPermissionCatalog,
  getPersonnel,
  getPlatformOrganisations,
  joinPlatformOrganisation,
  getProjects,
  linkOrganisationUserPersonnel,
  updateOrganisationRole,
  updateOrganisationSettings,
  removeOrganisationUser,
  unlinkOrganisationUserPersonnel,
  updateOrganisationUserEmail,
  updateOrganisationUserProjects,
  updateOrganisationUserRole,
  updatePlatformOrganisation,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/permissions';
import { formatPersonnelName } from '../utils/personnelDisplay';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import useResponsive from '../components/responsive/useResponsive';

function PermissionMatrix({ groups, value, onChange, readOnly }) {
  const { t } = useTranslation();
  const selected = new Set(value || []);

  const toggle = (key) => {
    if (readOnly) return;
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange([...next]);
  };

  return (
    <Box>
      {groups.map((group) => (
        <Box key={group.id} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t(group.labelKey)}
          </Typography>
          <FormGroup row sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {group.permissions.map((perm) => (
              <FormControlLabel
                key={perm.key}
                control={(
                  <Checkbox
                    checked={selected.has(perm.key)}
                    onChange={() => toggle(perm.key)}
                    disabled={readOnly}
                  />
                )}
                label={t(perm.labelKey)}
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
}

function roleTypeLabel(role, t) {
  if (role.isAdmin) return t('users.adminRole');
  if (role.isSystem) return t('users.systemRole');
  return t('users.customRole');
}

function Users() {
  const { t } = useTranslation();
  const { user, refreshUser, switchOrganisation } = useAuth();
  const { isMobile } = useResponsive();
  const isPlatformAdmin = user?.isPlatformAdmin;
  const canManage = canAccess(user, 'org.users.manage');
  const canRemove = canAccess(user, 'org.users.remove');
  const canViewUsers = canManage || canRemove;

  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [lunchDurationMinutes, setLunchDurationMinutes] = useState(30);
  const [lunchIsPartOfWorkHours, setLunchIsPartOfWorkHours] = useState(false);
  const [dailyWorkHoursFormat, setDailyWorkHoursFormat] = useState('HOURS');
  const [defaultWorkStartTime, setDefaultWorkStartTime] = useState('08:00');
  const [defaultWorkEndTime, setDefaultWorkEndTime] = useState('16:00');
  const [defaultUsedLunch, setDefaultUsedLunch] = useState(false);

  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);

  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserEmail, setEditUserEmail] = useState('');

  const [invites, setInvites] = useState([]);
  const [eligiblePersonnel, setEligiblePersonnel] = useState([]);
  const [invitePerson, setInvitePerson] = useState(null);
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteEmailDialogOpen, setInviteEmailDialogOpen] = useState(false);
  const [inviteEmailCapture, setInviteEmailCapture] = useState('');
  const [linkPersonnelOptions, setLinkPersonnelOptions] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetMember, setLinkTargetMember] = useState(null);
  const [linkPersonnelSelection, setLinkPersonnelSelection] = useState(null);

  const [platformOrgs, setPlatformOrgs] = useState([]);
  const [selectedPlatformOrgId, setSelectedPlatformOrgId] = useState('');
  const [platformOrgName, setPlatformOrgName] = useState('');
  const [platformOrgSlug, setPlatformOrgSlug] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [joiningOrg, setJoiningOrg] = useState(false);

  const loadMembers = async () => {
    const usersRes = await getOrganisationUsers();
    setMembers(usersRes.data);
  };

  const loadOrgAdminData = async () => {
    const [settingsRes, usersRes, rolesRes, catalogRes, projectsRes, invitesRes, eligibleRes] = await Promise.all([
      getOrganisationSettings(),
      getOrganisationUsers(),
      getOrganisationRoles(),
      getPermissionCatalog(),
      getProjects(),
      getOrganisationInvites(),
      getInviteEligiblePersonnel(),
    ]);
    setOrgName(settingsRes.data.name);
    setOrgSlug(settingsRes.data.slug || '');
    setLunchDurationMinutes(settingsRes.data.lunchDurationMinutes ?? 30);
    setLunchIsPartOfWorkHours(Boolean(settingsRes.data.lunchIsPartOfWorkHours));
    setDailyWorkHoursFormat(settingsRes.data.dailyWorkHoursFormat === 'TIME_RANGE' ? 'TIME_RANGE' : 'HOURS');
    setDefaultWorkStartTime(settingsRes.data.defaultWorkStartTime || '08:00');
    setDefaultWorkEndTime(settingsRes.data.defaultWorkEndTime || '16:00');
    setDefaultUsedLunch(Boolean(settingsRes.data.defaultUsedLunch));
    setMembers(usersRes.data);
    setRoles(rolesRes.data);
    setPermissionGroups(catalogRes.data.groups);
    setProjects(projectsRes.data);
    setInvites(invitesRes.data);
    setEligiblePersonnel(eligibleRes.data);
    const nonAdminRoles = rolesRes.data.filter((r) => !r.isAdmin);
    if (nonAdminRoles.length && !inviteRoleId) {
      setInviteRoleId(String(nonAdminRoles[0].id));
    }
  };

  const loadPlatformData = async () => {
    const response = await getPlatformOrganisations();
    setPlatformOrgs(response.data);
    if (response.data.length > 0) {
      const first = response.data[0];
      setSelectedPlatformOrgId(String(first.id));
      setPlatformOrgName(first.name);
      setPlatformOrgSlug(first.slug || '');
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const tasks = [];
      if (canManage) tasks.push(loadOrgAdminData());
      else if (canRemove) tasks.push(loadMembers());
      if (isPlatformAdmin) tasks.push(loadPlatformData());
      await Promise.all(tasks);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewUsers || isPlatformAdmin) {
      loadData();
    }
  }, [user]);

  if (!canViewUsers && !isPlatformAdmin) {
    return <Alert severity="warning">{t('users.adminOnly')}</Alert>;
  }

  const isLastOrgAdmin = (member) => (
    member.role?.isAdmin && members.filter((m) => m.role?.isAdmin).length <= 1
  );

  const canRemoveMember = (member) => (
    canRemove
    && member.user.id !== user?.id
    && !isLastOrgAdmin(member)
  );

  const handleRemoveMember = async (member) => {
    const confirmed = window.confirm(
      t('users.removeMemberConfirm', { email: member.user.email })
    );
    if (!confirmed) return;

    try {
      const response = await removeOrganisationUser(member.user.id);
      setSuccess(
        response.data.accountDeleted
          ? t('users.memberRemovedAccountDeleted')
          : t('users.memberRemoved')
      );
      if (canManage) {
        await loadOrgAdminData();
      } else {
        await loadMembers();
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.memberRemoveFailed'));
    }
  };

  const personnelOptionLabel = (person) => {
    const name = formatPersonnelName(person);
    return person.email ? `${name} — ${person.email}` : name;
  };

  const submitInvite = async (emailOverride = null) => {
    if (!invitePerson) {
      setError(t('users.invitePersonRequired'));
      return;
    }

    const payload = {
      personnelId: invitePerson.id,
      roleId: Number(inviteRoleId),
    };
    if (emailOverride) {
      payload.email = emailOverride;
    }

    const res = await createOrganisationInvite(payload);
    if (res.data.emailSent) {
      setSuccess(t('users.inviteSent', { email: emailOverride || invitePerson.email }));
    } else if (res.data.inviteUrl) {
      setInviteUrl(res.data.inviteUrl);
    }
    setInvitePerson(null);
    setInviteEmailCapture('');
    setInviteEmailDialogOpen(false);
    await loadOrgAdminData();
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setInviteUrl('');

    if (!invitePerson) {
      setError(t('users.invitePersonRequired'));
      return;
    }

    if (invitePerson.email) {
      try {
        await submitInvite();
      } catch (requestError) {
        setError(requestError.response?.data?.error || t('users.inviteEmailFailed'));
      }
      return;
    }

    setInviteEmailCapture('');
    setInviteEmailDialogOpen(true);
  };

  const handleInviteEmailDialogSubmit = async () => {
    try {
      await submitInvite(inviteEmailCapture.trim());
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.inviteEmailFailed'));
    }
  };

  const openLinkPersonnelDialog = async (member) => {
    setLinkTargetMember(member);
    setLinkPersonnelSelection(null);
    setLinkDialogOpen(true);
    try {
      const res = await getPersonnel({ params: { unlinkedOnly: true } });
      const memberEmail = member.user.email?.toLowerCase();
      const options = res.data.filter((person) => {
        if (person.userId) return false;
        if (!person.email) return true;
        return person.email.toLowerCase() === memberEmail;
      });
      setLinkPersonnelOptions(options);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.linkPersonnelFailed'));
    }
  };

  const handleLinkPersonnel = async () => {
    if (!linkTargetMember || !linkPersonnelSelection) {
      return;
    }
    try {
      await linkOrganisationUserPersonnel(linkTargetMember.user.id, {
        personnelId: linkPersonnelSelection.id,
      });
      setLinkDialogOpen(false);
      setSuccess(t('users.linkPersonnelSuccess'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.linkPersonnelFailed'));
    }
  };

  const handleUnlinkPersonnel = async (member) => {
    try {
      await unlinkOrganisationUserPersonnel(member.user.id);
      setSuccess(t('users.unlinkPersonnelSuccess'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.unlinkPersonnelFailed'));
    }
  };

  const renderMemberPersonnelLink = (member) => {
    if (!canManage || member.role?.isAdmin) {
      return null;
    }
    if (member.personnel) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {t('users.linkedPersonnel')}: {formatPersonnelName(member.personnel)}
          </Typography>
          <Button size="small" onClick={() => handleUnlinkPersonnel(member)}>
            {t('users.unlinkPersonnel')}
          </Button>
        </Stack>
      );
    }
    return (
      <Button size="small" variant="outlined" onClick={() => openLinkPersonnelDialog(member)}>
        {t('users.linkPersonnel')}
      </Button>
    );
  };

  const renderMemberRemoveButton = (member) => (
    canRemoveMember(member) ? (
      <Button
        size="small"
        color="error"
        variant="outlined"
        onClick={() => handleRemoveMember(member)}
        sx={{ flex: isMobile ? 1 : undefined }}
      >
        {t('users.removeMember')}
      </Button>
    ) : null
  );

  const inviteableRoles = roles.filter((r) => !r.isAdmin);

  const openRoleDialog = (role = null) => {
    setEditingRole(role);
    setRoleName(role?.name || '');
    setRolePermissions(Array.isArray(role?.permissions) ? role.permissions : []);
    setRoleDialogOpen(true);
  };

  const saveRole = async () => {
    try {
      if (editingRole) {
        await updateOrganisationRole(editingRole.id, { name: roleName, permissions: rolePermissions });
      } else {
        await createOrganisationRole({ name: roleName, permissions: rolePermissions });
      }
      setRoleDialogOpen(false);
      setSuccess(t('users.roleSaved'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.roleSaveFailed'));
    }
  };

  const handleRoleChange = async (member, roleId) => {
    try {
      await updateOrganisationUserRole(member.user.id, { roleId: Number(roleId) });
      setSuccess(t('users.roleUpdated'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.roleUpdateFailed'));
    }
  };

  const handleAllProjectsChange = async (member, allProjects) => {
    try {
      if (allProjects) {
        await updateOrganisationUserProjects(member.user.id, { allProjects: true });
      } else {
        await updateOrganisationUserProjects(member.user.id, {
          allProjects: false,
          projectIds: member.projectIds || [],
        });
      }
      setSuccess(t('users.projectsUpdated'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.projectsUpdateFailed'));
    }
  };

  const handleProjectChange = async (member, projectIds) => {
    try {
      await updateOrganisationUserProjects(member.user.id, { allProjects: false, projectIds });
      setSuccess(t('users.projectsUpdated'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.projectsUpdateFailed'));
    }
  };

  const renderMemberProjects = (member, autocompleteLabel) => {
    if (member.role?.isAdmin) {
      return (
        <Typography variant="body2" color="text.secondary">{t('users.allProjects')}</Typography>
      );
    }

    return (
      <Stack spacing={1}>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={Boolean(member.allProjectsAccess)}
              onChange={(e) => handleAllProjectsChange(member, e.target.checked)}
            />
          )}
          label={t('users.allProjects')}
        />
        {!member.allProjectsAccess ? (
          <Autocomplete
            multiple
            size="small"
            options={projects}
            getOptionLabel={(p) => p.name}
            value={projects.filter((p) => (member.projectIds || []).includes(p.id))}
            onChange={(_, val) => handleProjectChange(member, val.map((p) => p.id))}
            renderInput={(params) => <TextField {...params} label={autocompleteLabel} />}
          />
        ) : null}
      </Stack>
    );
  };

  const saveEmail = async () => {
    try {
      await updateOrganisationUserEmail(editUserId, { email: editUserEmail });
      setEmailDialogOpen(false);
      if (editUserId === user?.id) await refreshUser();
      setSuccess(t('users.emailSaved'));
      await loadOrgAdminData();
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('users.emailSaveFailed'));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('users.title')}</Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert> : null}
      {inviteUrl ? <Alert severity="success" sx={{ mb: 2 }}>{t('users.inviteCreated', { url: inviteUrl })}</Alert> : null}

      {isPlatformAdmin ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('users.platformOrganisations')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('users.platformOrganisationsHint')}
          </Typography>
          <Box component="form" onSubmit={async (event) => {
            event.preventDefault();
            const name = newOrgName.trim();
            if (!name) return;
            setCreatingOrg(true);
            setError('');
            try {
              const response = await createPlatformOrganisation({ name });
              await switchOrganisation(response.data.id);
            } catch (requestError) {
              setError(requestError.response?.data?.error || t('users.organisationCreateFailed'));
              setCreatingOrg(false);
            }
          }} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
            <TextField
              label={t('users.createOrganisation')}
              value={newOrgName}
              onChange={(event) => setNewOrgName(event.target.value)}
              required
              sx={{ flex: '1 1 220px' }}
            />
            <Button type="submit" variant="contained" disabled={creatingOrg} sx={{ mt: { xs: 0, sm: 1 } }}>
              {t('common.create')}
            </Button>
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('users.selectOrganisation')}</InputLabel>
            <Select
              value={selectedPlatformOrgId}
              label={t('users.selectOrganisation')}
              onChange={(e) => {
                setSelectedPlatformOrgId(e.target.value);
                const org = platformOrgs.find((o) => String(o.id) === e.target.value);
                if (org) {
                  setPlatformOrgName(org.name);
                  setPlatformOrgSlug(org.slug || '');
                }
              }}
            >
              {platformOrgs.map((org) => (
                <MenuItem key={org.id} value={String(org.id)}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label={t('users.organisationName')} value={platformOrgName} onChange={(e) => setPlatformOrgName(e.target.value)} />
          {selectedPlatformOrgId && !(user?.memberships || []).some((membership) => String(membership.organisationId) === selectedPlatformOrgId) ? (
            <Button
              variant="outlined"
              sx={{ mt: 2, mr: 1 }}
              disabled={joiningOrg}
              onClick={async () => {
                setJoiningOrg(true);
                setError('');
                try {
                  await joinPlatformOrganisation(Number(selectedPlatformOrgId));
                  await refreshUser();
                  setSuccess(t('users.joinedOrganisation'));
                } catch (requestError) {
                  setError(requestError.response?.data?.error || t('users.joinFailed'));
                } finally {
                  setJoiningOrg(false);
                }
              }}
            >
              {t('users.joinAsAdmin')}
            </Button>
          ) : null}
          <Button variant="contained" sx={{ mt: 2 }} onClick={async () => {
            await updatePlatformOrganisation(Number(selectedPlatformOrgId), { name: platformOrgName, slug: platformOrgSlug || null });
            setSuccess(t('users.organisationSaved'));
            await loadPlatformData();
          }}>
            {t('common.save')}
          </Button>
        </Paper>
      ) : null}

      {canManage ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={t('users.organisationSettings')} />
              <Tab label={t('users.rolesTab')} />
              <Tab label={t('users.members')} />
              <Tab label={t('users.pendingInvites')} />
            </Tabs>
          </Box>

          {tab === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Box component="form" onSubmit={async (e) => {
                e.preventDefault();
                await updateOrganisationSettings({
                  name: orgName,
                  slug: orgSlug || null,
                  lunchDurationMinutes: Number(lunchDurationMinutes),
                  lunchIsPartOfWorkHours,
                  dailyWorkHoursFormat,
                  defaultWorkStartTime,
                  defaultWorkEndTime,
                  defaultUsedLunch,
                });
                await refreshUser();
                setSuccess(t('users.organisationSaved'));
              }}>
                <TextField fullWidth margin="normal" label={t('users.organisationName')} value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
                <TextField fullWidth margin="normal" label={t('users.organisationSlug')} value={orgSlug} onChange={(e) => setOrgSlug(e.target.value)} />
                <FormControl fullWidth margin="normal">
                  <InputLabel id="daily-work-hours-format-label">{t('users.dailyWorkHoursFormat')}</InputLabel>
                  <Select
                    labelId="daily-work-hours-format-label"
                    label={t('users.dailyWorkHoursFormat')}
                    value={dailyWorkHoursFormat}
                    onChange={(e) => setDailyWorkHoursFormat(e.target.value)}
                  >
                    <MenuItem value="HOURS">{t('users.dailyWorkHoursFormatHours')}</MenuItem>
                    <MenuItem value="TIME_RANGE">{t('users.dailyWorkHoursFormatTimeRange')}</MenuItem>
                  </Select>
                </FormControl>
                {dailyWorkHoursFormat === 'TIME_RANGE' && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      margin="normal"
                      type="time"
                      label={t('users.defaultWorkStartTime', { defaultValue: 'Default work hours from' })}
                      value={defaultWorkStartTime}
                      onChange={(e) => setDefaultWorkStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      required
                      sx={{ flex: '1 1 160px' }}
                    />
                    <TextField
                      margin="normal"
                      type="time"
                      label={t('users.defaultWorkEndTime', { defaultValue: 'Default work hours till' })}
                      value={defaultWorkEndTime}
                      onChange={(e) => setDefaultWorkEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      required
                      sx={{ flex: '1 1 160px' }}
                    />
                  </Box>
                )}
                <TextField
                  fullWidth
                  margin="normal"
                  type="number"
                  label={t('users.lunchDurationMinutes')}
                  value={lunchDurationMinutes}
                  onChange={(e) => setLunchDurationMinutes(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  required
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={lunchIsPartOfWorkHours}
                      onChange={(e) => setLunchIsPartOfWorkHours(e.target.checked)}
                    />
                  )}
                  label={t('users.lunchIsPartOfWorkHours')}
                  sx={{ mt: 1, display: 'block' }}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={defaultUsedLunch}
                      onChange={(e) => setDefaultUsedLunch(e.target.checked)}
                    />
                  )}
                  label={t('users.defaultUsedLunch', { defaultValue: 'Lunch enabled by default' })}
                  sx={{ mt: 1, display: 'block' }}
                />
                <Button type="submit" variant="contained" sx={{ mt: 2 }}>{t('common.save')}</Button>
              </Box>
            </Paper>
          ) : null}

          {tab === 1 ? (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">{t('users.rolesTab')}</Typography>
                <Button variant="contained" onClick={() => openRoleDialog()} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                  {t('users.createRole')}
                </Button>
              </Box>
              {isMobile ? (
                <ResponsiveDataList
                  rows={roles}
                  getTitle={(role) => role.name}
                  getSubtitle={(role) => roleTypeLabel(role, t)}
                  renderActions={(role) => (
                    <>
                      <Button size="small" variant="outlined" onClick={() => openRoleDialog(role)} disabled={role.isAdmin} sx={{ flex: 1 }}>
                        {role.isAdmin ? t('users.viewPermissions') : t('common.edit')}
                      </Button>
                      {!role.isAdmin && !role.isSystem ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ flex: 1 }}
                          onClick={async () => {
                            await deleteOrganisationRole(role.id);
                            await loadOrgAdminData();
                          }}
                        >
                          {t('common.delete')}
                        </Button>
                      ) : null}
                    </>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('users.roleName')}</TableCell>
                        <TableCell>{t('users.type')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roles.map((role) => (
                        <TableRow key={role.id}>
                          <TableCell>{role.name}</TableCell>
                          <TableCell>{roleTypeLabel(role, t)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button size="small" onClick={() => openRoleDialog(role)} disabled={role.isAdmin}>
                                {role.isAdmin ? t('users.viewPermissions') : t('common.edit')}
                              </Button>
                              {!role.isAdmin && !role.isSystem ? (
                                <Button size="small" color="error" onClick={async () => {
                                  await deleteOrganisationRole(role.id);
                                  await loadOrgAdminData();
                                }}
                                >
                                  {t('common.delete')}
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          ) : null}

          {tab === 2 ? (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              {isMobile ? (
                <ResponsiveDataList
                  rows={members}
                  getTitle={(member) => member.user.email}
                  getSubtitle={(member) => member.role?.name}
                  renderFields={(member) => (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('users.role')}</InputLabel>
                        <Select
                          value={String(member.roleId)}
                          label={t('users.role')}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          disabled={isLastOrgAdmin(member)}
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.id} value={String(role.id)}>{role.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {renderMemberProjects(member, t('users.assignedProjects'))}
                      {renderMemberPersonnelLink(member)}
                    </Stack>
                  )}
                  renderActions={(member) => (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ width: '100%' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ flex: 1 }}
                        onClick={() => {
                          setEditUserId(member.user.id);
                          setEditUserEmail(member.user.email);
                          setEmailDialogOpen(true);
                        }}
                      >
                        {t('users.editEmail')}
                      </Button>
                      {renderMemberRemoveButton(member)}
                    </Stack>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('auth.email')}</TableCell>
                        <TableCell>{t('users.role')}</TableCell>
                        <TableCell>{t('users.assignedProjects')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.user.email}</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={String(member.roleId)}
                              onChange={(e) => handleRoleChange(member, e.target.value)}
                              disabled={isLastOrgAdmin(member)}
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.id} value={String(role.id)}>{role.name}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ minWidth: 280 }}>
                            <Stack spacing={1}>
                              {renderMemberProjects(member, t('users.projects'))}
                              {renderMemberPersonnelLink(member)}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button size="small" onClick={() => {
                                setEditUserId(member.user.id);
                                setEditUserEmail(member.user.email);
                                setEmailDialogOpen(true);
                              }}
                              >
                                {t('users.editEmail')}
                              </Button>
                              {renderMemberRemoveButton(member)}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {loading ? <Typography sx={{ mt: 2 }}>{t('common.loading')}</Typography> : null}
            </Paper>
          ) : null}

          {tab === 3 ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('users.invitePersonnelHint')}
              </Typography>
              <Box component="form" onSubmit={handleInviteSubmit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-start' }}>
                <Autocomplete
                  sx={{ minWidth: 280, flex: 1 }}
                  options={eligiblePersonnel}
                  value={invitePerson}
                  onChange={(_, value) => setInvitePerson(value)}
                  getOptionLabel={personnelOptionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label={t('users.selectPersonnel')} required />
                  )}
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('users.role')}</InputLabel>
                  <Select value={inviteRoleId} label={t('users.role')} onChange={(e) => setInviteRoleId(e.target.value)}>
                    {inviteableRoles.map((role) => (
                      <MenuItem key={role.id} value={String(role.id)}>{role.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button type="submit" variant="contained">{t('users.sendInvite')}</Button>
              </Box>
              {isMobile ? (
                <ResponsiveDataList
                  rows={invites}
                  getTitle={(invite) => (invite.personnel
                    ? formatPersonnelName(invite.personnel)
                    : invite.email)}
                  getSubtitle={(invite) => `${invite.role?.name || ''}${invite.email ? ` — ${invite.email}` : ''}`}
                  renderFields={(invite) => (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('users.expiresAt')}: {new Date(invite.expiresAt).toLocaleString()}
                    </Typography>
                  )}
                  renderActions={(invite) => (
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={async () => {
                        await deleteOrganisationInvite(invite.id);
                        await loadOrgAdminData();
                      }}
                    >
                      {t('users.revoke')}
                    </Button>
                  )}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('users.personnel')}</TableCell>
                        <TableCell>{t('auth.email')}</TableCell>
                        <TableCell>{t('users.role')}</TableCell>
                        <TableCell>{t('users.expiresAt')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.id}>
                          <TableCell>{invite.personnel ? formatPersonnelName(invite.personnel) : '—'}</TableCell>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>{invite.role?.name}</TableCell>
                          <TableCell>{new Date(invite.expiresAt).toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Button color="error" size="small" onClick={async () => {
                              await deleteOrganisationInvite(invite.id);
                              await loadOrgAdminData();
                            }}
                            >
                              {t('users.revoke')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          ) : null}
        </>
      ) : null}

      {!canManage && canRemove ? (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>{t('users.members')}</Typography>
          {isMobile ? (
            <ResponsiveDataList
              rows={members}
              getTitle={(member) => member.user.email}
              getSubtitle={(member) => member.role?.name}
              renderActions={(member) => renderMemberRemoveButton(member)}
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('auth.email')}</TableCell>
                    <TableCell>{t('users.role')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.user.email}</TableCell>
                      <TableCell>{member.role?.name}</TableCell>
                      <TableCell align="right">{renderMemberRemoveButton(member)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {loading ? <Typography sx={{ mt: 2 }}>{t('common.loading')}</Typography> : null}
        </Paper>
      ) : null}

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingRole ? t('users.editRole') : t('users.createRole')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label={t('users.roleName')}
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            disabled={editingRole?.isSystem}
          />
          <PermissionMatrix
            groups={permissionGroups}
            value={rolePermissions}
            onChange={setRolePermissions}
            readOnly={editingRole?.isAdmin}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>{t('common.cancel')}</Button>
          {!editingRole?.isAdmin ? (
            <Button variant="contained" onClick={saveRole}>{t('common.save')}</Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('users.editEmail')}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" type="email" label={t('auth.email')} value={editUserEmail} onChange={(e) => setEditUserEmail(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveEmail}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteEmailDialogOpen} onClose={() => setInviteEmailDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('users.inviteEmailTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('users.inviteEmailSubtitle', { name: invitePerson ? formatPersonnelName(invitePerson) : '' })}
          </Typography>
          <TextField
            fullWidth
            type="email"
            label={t('auth.email')}
            value={inviteEmailCapture}
            onChange={(e) => setInviteEmailCapture(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteEmailDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleInviteEmailDialogSubmit}>{t('users.sendInvite')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('users.linkPersonnel')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('users.linkPersonnelSubtitle', { email: linkTargetMember?.user?.email || '' })}
          </Typography>
          <Autocomplete
            options={linkPersonnelOptions}
            value={linkPersonnelSelection}
            onChange={(_, value) => setLinkPersonnelSelection(value)}
            getOptionLabel={personnelOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label={t('users.selectPersonnel')} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleLinkPersonnel} disabled={!linkPersonnelSelection}>
            {t('users.linkPersonnel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
